import React, { useState, useRef } from "react";
import { TEMPLATES, toHex, guessKind, qrUrl } from "../lib/templates";
import { toast } from "sonner";
import { titleCase } from "../lib/bls";

function Err({ msg }) { return msg ? <div className="field-error" role="alert" style={{ color: "#f87171", fontSize: 12, marginTop: 4 }}>{msg}</div> : null; }

// Four corner-snap buttons that set an {x,y} field pair from a {w,h} field pair already
// in cfg — a convenience on top of x/y, which stay freely editable regardless of which
// (or whether any) corner is currently active.
function CornerPicker({ fl, cfg, mutate }) {
  const w = Number(cfg[fl.w]) || 0, h = Number(cfg[fl.h]) || 0;
  const x = Number(cfg[fl.x]) || 0, y = Number(cfg[fl.y]) || 0;
  const corners = [
    { id: "tl", label: "Top-left", cx: 0, cy: 0 },
    { id: "tr", label: "Top-right", cx: 100 - w, cy: 0 },
    { id: "bl", label: "Bottom-left", cx: 0, cy: 100 - h },
    { id: "br", label: "Bottom-right", cx: 100 - w, cy: 100 - h },
  ];
  return (
    <div className="field">
      <label>{fl.label}</label>
      <div className="chips">
        {corners.map((c) => (
          <button key={c.id} type="button" className="chip" data-testid={`corner-${c.id}`}
            aria-pressed={x === c.cx && y === c.cy}
            onClick={() => mutate((cc) => { cc[fl.x] = c.cx; cc[fl.y] = c.cy; })}>{c.label}</button>
        ))}
      </div>
    </div>
  );
}

function clampPct(n, lo, hi) { n = Math.round(Number(n)); if (isNaN(n)) n = lo; return Math.max(lo, Math.min(hi, n)); }

// A mode select (Auto / Custom position) plus X/Y fields that are visibly DISABLED
// (not just documented in a label) whenever mode isn't "custom" — so it's never a
// silent no-op when someone edits X/Y while still on Auto.
function PlacementXY({ fl, cfg, mutate }) {
  const mode = cfg[fl.mode] || "auto";
  const isCustom = mode === "custom";
  const modeOptions = fl.modeOptions || [{ value: "auto", label: "Auto (inline in panel)" }, { value: "custom", label: "Custom position" }];
  const x = cfg[fl.x] == null ? "" : cfg[fl.x];
  const y = cfg[fl.y] == null ? "" : cfg[fl.y];
  return (
    <div className="field">
      <label>{fl.label}</label>
      <select value={mode} data-testid={`field-${fl.mode}`}
        onChange={(e) => mutate((c) => { c[fl.mode] = e.target.value; })}>
        {modeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
        <div className="field"><label>Custom X (%)</label>
          <input type="number" min={0} max={100} value={x} disabled={!isCustom} data-testid={`field-${fl.x}`}
            onChange={(e) => mutate((c) => { c[fl.x] = e.target.value === "" ? "" : parseInt(e.target.value, 10); })} /></div>
        <div className="field"><label>Custom Y (%)</label>
          <input type="number" min={0} max={100} value={y} disabled={!isCustom} data-testid={`field-${fl.y}`}
            onChange={(e) => mutate((c) => { c[fl.y] = e.target.value === "" ? "" : parseInt(e.target.value, 10); })} /></div>
      </div>
      {!isCustom && <div className="hint" style={{ marginTop: 4 }}>X/Y are greyed out because placement is Auto — switch to “Custom position” above to use them.</div>}
    </div>
  );
}

// A live diagram of the box {x,y,w,h} (percent of frame) occupies — drag or click inside
// it to move the box, or type exact numbers below. Always shows the CLAMPED, actually-
// rendered position (never lets the fields drift from what the video will really do).
function VideoBoxField({ fl, cfg, mutate, dims }) {
  const ref = useRef(null);
  const ratio = dims && dims.w && dims.h ? dims.w / dims.h : 16 / 9;
  const w = clampPct(cfg[fl.w], 10, 100);
  const h = clampPct(cfg[fl.h], 10, 100);
  const x = clampPct(cfg[fl.x], 0, 100 - w);
  const y = clampPct(cfg[fl.y], 0, 100 - h);

  const setBox = (patch) => mutate((c) => {
    const nw = clampPct(patch.w != null ? patch.w : w, 10, 100);
    const nh = clampPct(patch.h != null ? patch.h : h, 10, 100);
    c[fl.w] = nw; c[fl.h] = nh;
    c[fl.x] = clampPct(patch.x != null ? patch.x : x, 0, 100 - nw);
    c[fl.y] = clampPct(patch.y != null ? patch.y : y, 0, 100 - nh);
  });

  const dragTo = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * 100;
    const py = ((e.clientY - rect.top) / rect.height) * 100;
    setBox({ x: px - w / 2, y: py - h / 2 });
  };
  const onPointerDown = (e) => {
    e.preventDefault();
    dragTo(e);
    const onMove = (ev) => dragTo(ev);
    const onUp = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div className="field">
      <label>{fl.label}</label>
      <div ref={ref} onPointerDown={onPointerDown} data-testid={`videobox-diagram-${fl.key}`}
        style={{
          position: "relative", width: "100%", aspectRatio: ratio, background: "#0b1020",
          border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden", cursor: "move", touchAction: "none",
        }}>
        <div style={{
          position: "absolute", left: x + "%", top: y + "%", width: w + "%", height: h + "%",
          background: "rgba(59,130,246,.35)", border: "1.5px solid #3b82f6", boxSizing: "border-box",
        }} />
      </div>
      <div className="hint" style={{ marginTop: 4 }}>Drag the box above, or set exact numbers:</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
        <div className="field"><label>Width (%)</label>
          <input type="number" min={10} max={100} value={w} data-testid={`field-${fl.w}`}
            onChange={(e) => setBox({ w: e.target.value })} /></div>
        <div className="field"><label>Height (%)</label>
          <input type="number" min={10} max={100} value={h} data-testid={`field-${fl.h}`}
            onChange={(e) => setBox({ h: e.target.value })} /></div>
        <div className="field"><label>X position (%)</label>
          <input type="number" min={0} max={100} value={x} disabled={w >= 100} data-testid={`field-${fl.x}`}
            onChange={(e) => setBox({ x: e.target.value })} /></div>
        <div className="field"><label>Y position (%)</label>
          <input type="number" min={0} max={100} value={y} disabled={h >= 100} data-testid={`field-${fl.y}`}
            onChange={(e) => setBox({ y: e.target.value })} /></div>
      </div>
      {(w >= 100 || h >= 100) && (
        <div className="hint" style={{ marginTop: 4 }}>
          {w >= 100 && h >= 100 ? "Width and height are both 100% — the box already fills the frame, so X and Y have nowhere to move." :
            w >= 100 ? "Width is 100% — there's no horizontal room left, so X stays 0. Lower width to move it sideways." :
            "Height is 100% — there's no vertical room left, so Y stays 0. Lower height to move it up/down."}
        </div>
      )}
    </div>
  );
}

function Field({ fl, obj, keyName, onChange, error }) {
  const val = obj[keyName] == null ? "" : obj[keyName];
  if (fl.type === "checkbox") {
    return (
      <div className="field">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={!!obj[keyName]} onChange={(e) => onChange(e.target.checked)} data-testid={`field-${keyName}`} />
          <span>{fl.label}</span>
        </label>
      </div>
    );
  }
  if (fl.type === "keymap") {
    const m = (obj[keyName] && typeof obj[keyName] === "object") ? obj[keyName] : {};
    return (
      <div className="field">
        <label>{fl.label}</label>
        {(fl.keys || []).map((k) => (
          <div key={k} style={{ marginBottom: 6 }}>
            <div className="hint" style={{ margin: "2px 0" }}>{k}</div>
            <input type="url" spellCheck={false} value={m[k] || ""} placeholder="https://…"
              onChange={(e) => onChange({ ...m, [k]: e.target.value })} data-testid={`field-${keyName}-${k}`} />
          </div>
        ))}
        <Err msg={error} />
      </div>
    );
  }
  if (fl.type === "number") {
    return (
      <div className="field">
        <label>{fl.label}</label>
        <input type="number" min={fl.min} max={fl.max} value={val}
          onChange={(e) => onChange(e.target.value === "" ? "" : parseInt(e.target.value, 10))} data-testid={`field-${keyName}`} />
        <Err msg={error} />
      </div>
    );
  }
  const liveErr = error || (fl.https && val && !/^https:\/\/[^\s/]+/i.test(String(val)) ? "Must be an absolute https:// URL." : null);
  const over = fl.max && String(val).length > fl.max ? `Max ${fl.max} characters.` : null;
  if (fl.type === "color") {
    return (
      <div className="field">
        <label>{fl.label}</label>
        <div className="color-row">
          <input type="color" value={toHex(val)} onChange={(e) => onChange(e.target.value)} data-testid={`color-${keyName}`} />
          <input type="text" spellCheck={false} value={val} onChange={(e) => onChange(e.target.value)} />
        </div>
      </div>
    );
  }
  if (fl.type === "textarea") {
    return (
      <div className="field">
        <label>{fl.label}</label>
        <textarea spellCheck={false} value={val} onChange={(e) => onChange(e.target.value)} data-testid={`field-${keyName}`} />
        <Err msg={liveErr || over} />
      </div>
    );
  }
  if (fl.type === "select") {
    return (
      <div className="field">
        <label>{fl.label}</label>
        <select value={val} onChange={(e) => onChange(e.target.value)} data-testid={`field-${keyName}`}>
          {(fl.options || []).map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div className="field">
      <label>{fl.label}</label>
      <input type={fl.type === "url" ? "url" : "text"} spellCheck={false} value={val}
             onChange={(e) => onChange(e.target.value)} data-testid={`field-${keyName}`} />
      {fl.preview === "titlecase" && (
        <div className="hint" data-testid={`preview-${keyName}`} style={{ marginTop: 4 }}>
          Shown as: <b>{obj.keepCase ? String(val) : titleCase(val)}</b>
        </div>
      )}
      <Err msg={liveErr || over} />
    </div>
  );
}

function ImageField({ fl, obj, keyName, onChange, onUpload }) {
  const cur = obj[keyName] || "";
  const isLocal = String(cur).slice(0, 5) === "data:" || String(cur).slice(0, 5) === "blob:";
  // Picking a file shows it instantly via a local blob: URL, then uploads it right away in
  // the background — no separate click required (a blob: URL only exists in this tab, so
  // if it got saved into a creative as-is, it would silently break on the next page load).
  // `pending` only reappears if the auto-upload fails, as a manual retry affordance.
  const [pending, setPending] = useState(null); // { file, localUrl } | null
  const [uploading, setUploading] = useState(false);
  const upload = async (file, localUrl) => {
    setUploading(true);
    try {
      const res = await onUpload(file);
      onChange(res.url);
      URL.revokeObjectURL(localUrl);
      setPending(null);
      toast.success("Image uploaded");
    } catch (err) { setPending({ file, localUrl }); toast.error("Upload failed — click Retry"); }
    finally { setUploading(false); }
  };
  return (
    <div className="field">
      <label>{fl.label}</label>
      <div className="uprow">
        <input type="url" placeholder={pending ? pending.file.name : "https://…"} spellCheck={false}
               value={isLocal ? "" : cur} disabled={!!pending || uploading}
               onChange={(e) => { setPending(null); onChange(e.target.value); }} data-testid={`img-url-${keyName}`} />
        {uploading ? (
          <span className="upload-loading" data-testid={`img-uploading-${keyName}`}><span className="spinner" />Uploading…</span>
        ) : pending ? (
          <button type="button" className="btn primary" data-testid={`img-save-${keyName}`}
            onClick={() => upload(pending.file, pending.localUrl)}>Retry</button>
        ) : (
          <label className="btn filebtn">Choose file
            <input type="file" accept="image/*" data-testid={`img-upload-${keyName}`}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) return;
                const localUrl = URL.createObjectURL(f);
                onChange(localUrl);
                upload(f, localUrl);
              }} />
          </label>
        )}
      </div>
      {pending && <div className="hint">Upload failed for “{pending.file.name}” — still only previewing locally.</div>}
    </div>
  );
}

function VideoUrlField({ v, onChange, onUpload }) {
  const cur = v.url || "";
  const isLocal = cur.slice(0, 5) === "data:" || cur.slice(0, 5) === "blob:";
  // Picking a file shows it instantly via a local blob: URL, then uploads it right away in
  // the background — no separate click required (a blob: URL only exists in this tab, so
  // if it got saved into a creative as-is, it would silently break on the next page load).
  // `pending` only reappears if the auto-upload fails, as a manual retry affordance.
  const [pending, setPending] = useState(null); // { file, localUrl } | null
  const [uploading, setUploading] = useState(false);
  const upload = async (file, localUrl) => {
    setUploading(true);
    try {
      const res = await onUpload(file);
      onChange(res.url, guessKind(file.name));
      URL.revokeObjectURL(localUrl);
      setPending(null);
      toast.success("Video uploaded");
    } catch (err) { setPending({ file, localUrl }); toast.error("Upload failed — click Retry"); }
    finally { setUploading(false); }
  };
  return (
    <div className="field">
      <label>URL (MP4 or HLS .m3u8)</label>
      <div className="uprow">
        <input type="url" spellCheck={false} value={isLocal ? "" : cur}
               placeholder={pending ? pending.file.name : "https://…"} disabled={!!pending || uploading}
               onChange={(e) => { setPending(null); onChange(e.target.value, guessKind(e.target.value)); }} data-testid="field-video-url" />
        {uploading ? (
          <span className="upload-loading" data-testid="video-uploading"><span className="spinner" />Uploading…</span>
        ) : pending ? (
          <button type="button" className="btn primary" data-testid="video-save"
            onClick={() => upload(pending.file, pending.localUrl)}>Retry</button>
        ) : (
          <label className="btn filebtn">Choose file
            <input type="file" accept="video/*" data-testid="video-upload"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) return;
                const localUrl = URL.createObjectURL(f);
                onChange(localUrl, guessKind(f.name));
                upload(f, localUrl);
              }} />
          </label>
        )}
      </div>
      <div className="hint">{uploading ? "Uploading…" : pending ? `Upload failed for “${pending.file.name}” — still only previewing locally.` : "Upload from your computer, or paste a hosted URL."}</div>
    </div>
  );
}

function QrGen({ fl, value, onChange }) {
  const src = qrUrl(value);
  return (
    <div className="field">
      <label>{fl.label}</label>
      <div className="uprow">
        <input type="url" placeholder="https://your-landing-page.com" spellCheck={false} value={value}
               onChange={(e) => onChange(e.target.value)} data-testid={`qrgen-${fl.key}`} />
        {src && <img src={src} alt="QR preview" width={40} height={40}
               style={{ borderRadius: 6, background: "#fff", padding: 2, flex: "0 0 auto" }} data-testid="qrgen-preview" />}
      </div>
      <div className="hint">A scannable QR is generated automatically and hosted — no upload needed.</div>
    </div>
  );
}

function Renditions({ bg, mutate, onUpload }) {
  const vids = bg.videos || [];
  return (
    <div>
      <div className="items">
        {vids.map((v, idx) => (
          <div className="item" key={idx}>
            <div className="item-head">
              <span className="lbl">Rendition {idx + 1}</span>
              <button className="del" type="button" data-testid={`rendition-del-${idx}`}
                onClick={() => mutate((c) => { c.background.videos.splice(idx, 1); })}>×</button>
            </div>
            <VideoUrlField v={v} onUpload={onUpload}
              onChange={(url, kind) => mutate((c) => { c.background.videos[idx].url = url; c.background.videos[idx].kind = kind; })} />
            <div className="field">
              <label>Type</label>
              <select value={v.kind} onChange={(e) => mutate((c) => { c.background.videos[idx].kind = e.target.value; })}>
                <option value="mp4">MP4 (progressive)</option>
                <option value="hls">HLS (adaptive)</option>
              </select>
            </div>
            <Field fl={{ label: "Bitrate (kbps)", type: "text" }} obj={v} keyName="bitrate"
              onChange={(val) => mutate((c) => { c.background.videos[idx].bitrate = val; })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <Field fl={{ label: "Width", type: "text" }} obj={v} keyName="w" onChange={(val) => mutate((c) => { c.background.videos[idx].w = val; })} />
              <Field fl={{ label: "Height", type: "text" }} obj={v} keyName="h" onChange={(val) => mutate((c) => { c.background.videos[idx].h = val; })} />
            </div>
          </div>
        ))}
      </div>
      <button className="addbtn" type="button" data-testid="add-rendition"
        onClick={() => mutate((c) => { if (!c.background.videos) c.background.videos = []; c.background.videos.push({ url: "", kind: "mp4", bitrate: 1500, w: 1280, h: 720 }); })}>
        + Add rendition
      </button>
    </div>
  );
}

export default function InspectorForm({ tid, schema, cfg, setCfg, onUpload, resetIndex, errors = {} }) {
  const t = schema || TEMPLATES[tid];
  const mutate = (fn) => {
    const c = JSON.parse(JSON.stringify(cfg));
    fn(c);
    setCfg(c);
  };
  // Templates with their own separate "Product image" box (split-5050 and friends) use
  // Background purely as the VIDEO source for one side — switching it to "Image" replaces
  // the video outright rather than adding a second image alongside it, which is a common
  // mix-up. Surface that distinction right where the toggle is, for those templates only.
  const hasOwnImageBox = !!(t.content || []).some((s) => (s.fields || []).some((f) => f.type === "videobox" && f.key === "imageBox"));

  return (
    <div className="form" data-testid="inspector-form">
      {cfg.background && typeof cfg.background === "object" && (<>
      {/* Playback */}
      <div className="group">
        <div className="eyebrow">Playback</div>
        {cfg.background.type === "image" ? (
          <div className="field">
            <label>Ad duration (seconds)</label>
            <input type="number" min="1" max="120" value={cfg.duration == null ? 10 : cfg.duration}
              onChange={(e) => mutate((c) => { c.duration = parseInt(e.target.value, 10) || 1; })} data-testid="field-duration" />
            <div className="hint">Image backgrounds have no length, so the ad runs for this long. Sets the VAST &lt;Duration&gt;.</div>
          </div>
        ) : (
          <div className="hint" data-testid="duration-auto">Duration follows the video length automatically (currently {cfg.duration || "—"}s). Sets the VAST &lt;Duration&gt;.</div>
        )}
      </div>

      {/* Background */}
      <div className="group">
        <div className="eyebrow">Background</div>
        <div className="seg">
          {[["image", "Image"], ["video", "Video"], ["partner", "Partner VAST"]].map(([tp, lbl]) => (
            <button key={tp} type="button" aria-pressed={cfg.background.type === tp} data-testid={`bg-type-${tp}`}
              onClick={() => mutate((c) => {
                const b = c.background; b.type = tp; // switch output only — keep all inputs in memory
                if (tp === "video" && (!b.videos || !b.videos.length)) b.videos = [{ url: "", kind: "mp4", bitrate: 1500, w: 1280, h: 720 }];
                if (tp === "partner" && b.partnerVast == null) b.partnerVast = "";
              })}>{lbl}</button>
          ))}
        </div>
        {hasOwnImageBox && (
          <div className="hint" data-testid="bg-image-vs-product-hint" style={{ marginTop: 4 }}>
            This is also where the split's <b>picture side</b> gets its image: switch to <b>Image</b>, upload your picture, then switch back to <b>Video</b> to resume video playback — the uploaded picture stays in memory and keeps showing on its side either way.
          </div>
        )}
        {cfg.background.type === "video" ? (
          <>
            <Renditions bg={cfg.background} mutate={mutate} onUpload={onUpload} />
            <div className="hint">Add one HLS master for adaptive streaming, and/or several MP4 renditions at different bitrates — the player picks. Preview uses HLS if present, else the first MP4.</div>
          </>
        ) : cfg.background.type === "partner" ? (
          <>
            <Field fl={{ label: "Partner VAST tag URL", type: "url" }} obj={cfg.background} keyName="partnerVast"
              onChange={(val) => mutate((c) => { c.background.partnerVast = val; })} />
            <div className="hint">Paste a demand partner's VAST tag URL. On export/serve we fetch it live (following wrapper redirects up to 5 hops), play their video behind our interactive overlay, and merge their tracking so their video events fire alongside ours.</div>
          </>
        ) : (
          <>
            <ImageField fl={{ key: "image", label: "Background image (URL or upload)" }} obj={cfg.background} keyName="image"
              onChange={(val) => mutate((c) => { c.background.image = val; })} onUpload={onUpload} />
            <div className="hint">The image is the <b>primary full-screen background</b> (it fills the frame like a video would). Because a linear slot needs a video, the exported tag includes an invisible black base video underneath for non-SIMID players — no companion ad.</div>
          </>
        )}
      </div>
      </>)}

      {/* Layout */}
      {t.layouts && t.layouts.length > 0 && (
        <div className="group">
          <div className="eyebrow">Layout</div>
          <div className="chips">
            {t.layouts.map((l) => (
              <button key={l.id} type="button" className="chip" aria-pressed={cfg.layout === l.id} data-testid={`layout-${l.id}`}
                onClick={() => mutate((c) => { c.layout = l.id; })}>{l.name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {t.content.map((section) => (
        <div className="group" key={section.group}>
          <div className="eyebrow">{section.group}</div>
          {section.fields && section.fields.map((fl) => (
            fl.type === "image"
              ? <ImageField key={fl.key} fl={fl} obj={cfg} keyName={fl.key} onUpload={onUpload}
                  onChange={(v) => mutate((c) => { c[fl.key] = v; })} />
              : fl.type === "qrgen"
              ? <QrGen key={fl.key} fl={fl} value={cfg[fl.key] || ""} onChange={(v) => mutate((c) => { c[fl.key] = v; c.qrImg = qrUrl(v); })} />
              : fl.type === "cornerpicker"
              ? <CornerPicker key={fl.key} fl={fl} cfg={cfg} mutate={mutate} />
              : fl.type === "videobox"
              ? <VideoBoxField key={fl.key} fl={fl} cfg={cfg} mutate={mutate} dims={t.dims} />
              : fl.type === "placementxy"
              ? <PlacementXY key={fl.key} fl={fl} cfg={cfg} mutate={mutate} />
              : <Field key={fl.key} fl={fl} obj={cfg} keyName={fl.key} error={errors[fl.key]} onChange={(v) => mutate((c) => { c[fl.key] = v; })} />
          ))}
          {section.list && errors[section.list] && <Err msg={errors[section.list]} />}
          {section.list && (
            <>
              <div className="items">
                {cfg[section.list].map((iv, idx) => (
                  <div className="item" key={idx}>
                    <div className="item-head">
                      <span className="lbl">{section.itemLabel} {idx + 1}</span>
                      {cfg[section.list].length > section.min && (
                        <button className="del" type="button" data-testid={`${section.list}-del-${idx}`}
                          onClick={() => mutate((c) => { c[section.list].splice(idx, 1); if (resetIndex) resetIndex(); })}>×</button>
                      )}
                    </div>
                    {section.item.map((fl) => (
                      fl.type === "image"
                        ? <ImageField key={fl.key} fl={fl} obj={iv} keyName={fl.key} onChange={(v) => mutate((c) => { c[section.list][idx][fl.key] = v; })} onUpload={onUpload} />
                        : <Field key={fl.key} fl={fl} obj={iv} keyName={fl.key} onChange={(v) => mutate((c) => { c[section.list][idx][fl.key] = v; })} />
                    ))}
                  </div>
                ))}
              </div>
              {cfg[section.list].length < section.max && (
                <button className="addbtn" type="button" data-testid={`add-${section.list}`}
                  onClick={() => mutate((c) => {
                    const blank = {};
                    section.item.forEach((fl) => { blank[fl.key] = fl.type === "color" ? "#888888" : fl.type === "checkbox" ? false : ""; });
                    c[section.list].push(blank);
                  })}>+ Add {section.itemLabel.toLowerCase()}</button>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
